import { Popover as PopoverPrimitive } from "@base-ui/react/popover"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/modules/lib/utils"

const popoverItemVariants = cva(
  "inline-flex min-h-8.5 min-w-45 cursor-pointer items-center gap-2 rounded-2xl py-2 pr-8 pl-8 label-medium outline-none focus-visible:ring-[3px] disabled:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "text-textOnSurface-subtle hover:bg-interactive-background-primary-default hover:text-interactive-text-primary-default focus-visible:ring-surface-border-primary-muted disabled:text-textOnSurface-disabled",
        destructive:
          "text-interactive-text-negative-intense hover:bg-interactive-background-negative-faded focus-visible:ring-interactive-border-negative-faded disabled:text-interactive-text-negative-disabled",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

function Popover({ ...props }: PopoverPrimitive.Root.Props) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />
}

function PopoverTrigger({ ...props }: PopoverPrimitive.Trigger.Props) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
}

function PopoverContent({
  className,
  align = "center",
  alignOffset = 0,
  side = "bottom",
  sideOffset = 4,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<PopoverPrimitive.Positioner.Props, "align" | "alignOffset" | "side" | "sideOffset">) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className="isolate z-50"
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={cn(
            "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 flex origin-(--transform-origin) flex-col rounded-3xl bg-surface-background-float px-2 py-1 label-medium text-textOnSurface-subtle shadow-md ring-1 ring-surface-border-gray-default outline-hidden duration-100",
            className,
          )}
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  )
}

function PopoverItem({
  className,
  variant,
  ...props
}: React.ComponentProps<"button"> & VariantProps<typeof popoverItemVariants>) {
  return (
    <button
      data-slot="popover-item"
      className={cn(popoverItemVariants({ variant }), className)}
      {...props}
    />
  )
}

// Not used
function PopoverHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="popover-header" className={cn("flex flex-col gap-0.5", className)} {...props} />
  )
}

// Not used
function PopoverTitle({ className, ...props }: PopoverPrimitive.Title.Props) {
  return (
    <PopoverPrimitive.Title
      data-slot="popover-title"
      className={cn("cn-font-heading font-medium", className)}
      {...props}
    />
  )
}

// Not used
function PopoverDescription({ className, ...props }: PopoverPrimitive.Description.Props) {
  return (
    <PopoverPrimitive.Description
      data-slot="popover-description"
      className={cn("text-textOnSurface-subtle", className)}
      {...props}
    />
  )
}

export {
  Popover,
  popoverItemVariants,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverItem,
  PopoverTitle,
  PopoverTrigger,
}
